import { Fragment, useEffect, useRef, useState } from 'react'
import { useAtomValue, useSetAtom, useAtom } from 'jotai'
import { useNavigate } from 'react-router-dom'
import { AppShell } from '../../components/AppShell'
import { pageLinks } from '../../pageLinks'
import {
  listingsAtom,
  fetchedAtom,
  searchAtom,
  statusFilterAtom,
  loadingAtom,
  errorAtom,
  filteredListingsAtom,
  summaryAtom,
  fetchListingsAtom,
  prefillListingAtom,
  type AmazonListing,
  type StatusFilter,
} from '../../stores/AmazonListingsStore'

function StatusBadge({ status }: { status: string }) {
  const normalized = status.toLowerCase()
  if (normalized === 'active') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-[#DCFCE7] text-[#166534]">
        <span className="size-1.5 rounded-full bg-[#22C55E]" />
        Active
      </span>
    )
  }
  if (normalized === 'inactive') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-[#F5F5F5] text-[#52525B]">
        <span className="size-1.5 rounded-full bg-[#A1A1AA]" />
        Inactive
      </span>
    )
  }
  // Incomplete
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-[#FEF9C3] text-[#854D0E]">
      <span className="size-1.5 rounded-full bg-[#EAB308]" />
      {status || 'Incomplete'}
    </span>
  )
}

/** Small section header used in the detail panel */
function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <h4 className="text-[11px] font-semibold text-[#71717A] uppercase tracking-wider mb-2">
      {children}
    </h4>
  )
}

/** Key-value row for product info */
function InfoRow({ label, value }: { label: string; value: string }) {
  if (!value) return null
  return (
    <div className="flex items-baseline justify-between py-1">
      <span className="text-[12px] text-[#A1A1AA] shrink-0">{label}</span>
      <span className="text-[13px] text-[#18181B] text-right ml-3 truncate">{value}</span>
    </div>
  )
}

/** Expandable detail panel shown below a listing row */
function ListingDetail({ listing }: { listing: AmazonListing }) {
  const [descExpanded, setDescExpanded] = useState(false)
  const [isDescClamped, setIsDescClamped] = useState(false)
  const descRef = useRef<HTMLParagraphElement>(null)

  // Detect if the description text is actually overflowing the 4-line clamp
  useEffect(() => {
    const el = descRef.current
    if (!el) return
    // scrollHeight > clientHeight means text is clamped/truncated
    setIsDescClamped(el.scrollHeight > el.clientHeight + 1)
  }, [listing.description])

  const hasBulletPoints = listing.bulletPoints && listing.bulletPoints.length > 0
  const hasDescription = !!listing.description
  const hasKeywords = !!listing.searchTerms
  const hasContent = hasBulletPoints || hasDescription || hasKeywords

  // Product info fields
  const infoFields = [
    { label: 'Brand', value: listing.brand },
    { label: 'Manufacturer', value: listing.manufacturer },
    { label: 'Model #', value: listing.modelNumber },
    { label: 'Material', value: listing.material },
    { label: 'Country', value: listing.countryOfOrigin },
  ].filter((f) => f.value)

  const identifiers = [
    { label: 'UPC', value: listing.upc },
    { label: 'EAN', value: listing.ean },
  ].filter((f) => f.value)

  // Pricing info
  const currentPrice = listing.currentPrice
  const regularPrice = listing.regularPrice
  const salePrice = listing.salePrice
  const hasPricingInfo = currentPrice || regularPrice || salePrice

  const formatDate = (d: string) => {
    if (!d) return ''
    try {
      const date = new Date(d)
      if (isNaN(date.getTime())) return d
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    } catch {
      return d
    }
  }

  return (
    <div className="px-6 py-5 bg-[#FAFAFA] border-t border-[#E5E5E5]">
      <div className="flex gap-8">
        {/* Left column — listing content */}
        <div className="flex-1 min-w-0 space-y-5">
          {!hasContent && (
            <p className="text-[13px] text-[#A1A1AA] italic">
              No listing content available. Content is fetched from the Listings Items API.
            </p>
          )}

          {/* Bullet Points */}
          {hasBulletPoints && (
            <div>
              <SectionLabel>Bullet Points</SectionLabel>
              <ol className="space-y-1.5 pl-0">
                {listing.bulletPoints.map((bp, idx) => (
                  <li key={idx} className="flex gap-2.5 text-[13px] text-[#3F3F46]">
                    <span className="text-[12px] text-[#A1A1AA] font-mono shrink-0 w-4 text-right">{idx + 1}.</span>
                    <span className="leading-relaxed">{bp}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}

          {/* Description */}
          {hasDescription && (
            <div>
              <SectionLabel>Description</SectionLabel>
              <div className="relative">
                <p
                  ref={descRef}
                  className={`text-[13px] text-[#52525B] leading-relaxed whitespace-pre-line ${
                    !descExpanded ? 'line-clamp-4' : ''
                  }`}
                >
                  {listing.description}
                </p>
                {(isDescClamped || descExpanded) && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      setDescExpanded(!descExpanded)
                    }}
                    className="text-[12px] text-[#2563EB] hover:underline mt-1 cursor-pointer"
                  >
                    {descExpanded ? 'Show less' : 'Show more'}
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Search Terms / Keywords */}
          {hasKeywords && (
            <div>
              <SectionLabel>Search Terms / Keywords</SectionLabel>
              <p className="text-[13px] text-[#52525B] leading-relaxed">
                {listing.searchTerms}
              </p>
            </div>
          )}
        </div>

        {/* Right column — product info, identifiers, pricing */}
        <div className="w-[280px] shrink-0 space-y-5">
          {/* Product Info */}
          {infoFields.length > 0 && (
            <div>
              <SectionLabel>Product Info</SectionLabel>
              <div className="space-y-0.5">
                {infoFields.map((f) => (
                  <InfoRow key={f.label} label={f.label} value={f.value} />
                ))}
              </div>
            </div>
          )}

          {/* Identifiers */}
          {identifiers.length > 0 && (
            <div>
              <SectionLabel>Identifiers</SectionLabel>
              <div className="space-y-0.5">
                {identifiers.map((f) => (
                  <InfoRow key={f.label} label={f.label} value={f.value} />
                ))}
              </div>
            </div>
          )}

          {/* Pricing */}
          {hasPricingInfo && (
            <div>
              <SectionLabel>Pricing</SectionLabel>
              <div className="space-y-0.5">
                {currentPrice && <InfoRow label="Current" value={`$${currentPrice}`} />}
                {regularPrice && regularPrice !== currentPrice && (
                  <InfoRow label="Regular" value={`$${regularPrice}`} />
                )}
                {salePrice && <InfoRow label="Sale" value={`$${salePrice}`} />}
                {listing.saleStartDate && (
                  <InfoRow label="Sale start" value={formatDate(listing.saleStartDate)} />
                )}
                {listing.saleEndDate && (
                  <InfoRow label="Sale end" value={formatDate(listing.saleEndDate)} />
                )}
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  )
}

export default function AmazonListings() {
  const listings = useAtomValue(listingsAtom)
  const fetched = useAtomValue(fetchedAtom)
  const loading = useAtomValue(loadingAtom)
  const error = useAtomValue(errorAtom)
  const filtered = useAtomValue(filteredListingsAtom)
  const summary = useAtomValue(summaryAtom)
  const [search, setSearch] = useAtom(searchAtom)
  const [statusFilter, setStatusFilter] = useAtom(statusFilterAtom)
  const fetchListings = useSetAtom(fetchListingsAtom)
  const setPrefill = useSetAtom(prefillListingAtom)
  const navigate = useNavigate()

  // Track which listing row is expanded (by unique key)
  const [expandedKey, setExpandedKey] = useState<string | null>(null)

  const rowKey = (listing: AmazonListing, i: number) =>
    `${listing.asin}-${listing.sellerSku}-${i}`

  const handleRowClick = (key: string) => {
    setExpandedKey((prev) => (prev === key ? null : key))
  }

  const handleGenerateLabel = (listing: AmazonListing) => {
    setPrefill(listing)
    navigate('/')
  }

  return (
    <AppShell activeNav="amazon">
      <div className="flex-1 overflow-y-auto">
        <div className="px-6 py-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-1">
            <div>
              <h1 className="text-lg font-semibold text-[#18181B]">Amazon Listings</h1>
              <p className="text-[13px] text-[#71717A] mt-1">View your Amazon seller inventory and listing status</p>
            </div>
            <button
              onClick={() => fetchListings()}
              disabled={loading}
              className="px-4 h-9 bg-[#18181B] text-white rounded-lg text-sm font-medium flex items-center gap-2 hover:bg-[#3F3F46] transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <svg className="size-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
                </svg>
              ) : (
                <svg className="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
                </svg>
              )}
              {loading ? 'Fetching...' : fetched ? 'Refresh' : 'Fetch Listings'}
            </button>
          </div>

          {/* Loading state */}
          {loading && (
            <div className="mt-8 flex flex-col items-center justify-center py-16">
              <div className="size-12 rounded-full bg-[#FFFBEB] flex items-center justify-center mb-4">
                <svg className="size-6 text-[#F59E0B] animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
                </svg>
              </div>
              <h2 className="text-[15px] font-semibold text-[#18181B] mb-1">Fetching listings from Amazon...</h2>
              <p className="text-[13px] text-[#71717A] text-center max-w-sm">
                This may take up to 30 seconds while we generate and download your listings report.
              </p>
            </div>
          )}

          {/* Error state */}
          {error && !loading && (
            <div className="mt-8 flex flex-col items-center justify-center py-16">
              <div className="size-12 rounded-full bg-[#FEF2F2] flex items-center justify-center mb-4">
                <svg className="size-6 text-[#EF4444]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z" />
                </svg>
              </div>
              <h2 className="text-[15px] font-semibold text-[#EF4444] mb-1">Failed to fetch listings</h2>
              <p className="text-[13px] text-[#71717A] mb-4 text-center max-w-sm">{error}</p>
              <button
                onClick={() => fetchListings()}
                className="px-4 h-9 bg-[#18181B] text-white rounded-lg text-sm font-medium hover:bg-[#3F3F46] transition-colors cursor-pointer"
              >
                Try Again
              </button>
            </div>
          )}

          {/* Empty state (before first fetch) */}
          {!loading && !error && !fetched && (
            <div className="mt-8 flex flex-col items-center justify-center py-16">
              <div className="size-12 rounded-full bg-[#F5F5F5] flex items-center justify-center mb-4">
                <svg className="size-6 text-[#A1A1AA]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 21v-7.5a.75.75 0 0 1 .75-.75h3a.75.75 0 0 1 .75.75V21m-4.5 0H2.36m11.14 0H18m0 0h3.64m-1.39 0V9.349M3.75 21V9.349m0 0a3.001 3.001 0 0 0 3.75-.615A2.993 2.993 0 0 0 9.75 9.75c.896 0 1.7-.393 2.25-1.016a2.993 2.993 0 0 0 2.25 1.016c.896 0 1.7-.393 2.25-1.015a3.001 3.001 0 0 0 3.75.614m-16.5 0a3.004 3.004 0 0 1-.621-4.72l1.189-1.19A1.5 1.5 0 0 1 5.378 3h13.243a1.5 1.5 0 0 1 1.06.44l1.19 1.189a3 3 0 0 1-.621 4.72M6.75 18h3.75a.75.75 0 0 0 .75-.75V13.5a.75.75 0 0 0-.75-.75H6.75a.75.75 0 0 0-.75.75v3.75c0 .414.336.75.75.75Z" />
                </svg>
              </div>
              <h2 className="text-[15px] font-semibold text-[#18181B] mb-1">Connect to Amazon</h2>
              <p className="text-[13px] text-[#71717A] mb-4 text-center max-w-sm">
                Click the button above to fetch your Amazon seller listings via SP-API.
              </p>
            </div>
          )}

          {/* Listings table */}
          {!loading && !error && fetched && (
            <>
              {/* Summary & search bar */}
              <div className="flex items-center justify-between mt-5 mb-4">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setStatusFilter('all')}
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[12px] font-medium transition-colors cursor-pointer ${
                      statusFilter === 'all'
                        ? 'bg-[#18181B] text-white'
                        : 'text-[#52525B] hover:bg-[#F5F5F5]'
                    }`}
                  >
                    All
                    <span className={`text-[11px] ${statusFilter === 'all' ? 'text-white/70' : 'text-[#A1A1AA]'}`}>{listings.length}</span>
                  </button>
                  <button
                    onClick={() => setStatusFilter(statusFilter === 'active' ? 'all' : 'active')}
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[12px] font-medium transition-colors cursor-pointer ${
                      statusFilter === 'active'
                        ? 'bg-[#DCFCE7] text-[#166534]'
                        : 'text-[#52525B] hover:bg-[#F5F5F5]'
                    }`}
                  >
                    <span className={`size-1.5 rounded-full ${statusFilter === 'active' ? 'bg-[#22C55E]' : 'bg-[#22C55E]/50'}`} />
                    Active
                    <span className={`text-[11px] ${statusFilter === 'active' ? 'text-[#166534]/70' : 'text-[#A1A1AA]'}`}>{summary.active}</span>
                  </button>
                  <button
                    onClick={() => setStatusFilter(statusFilter === 'inactive' ? 'all' : 'inactive')}
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[12px] font-medium transition-colors cursor-pointer ${
                      statusFilter === 'inactive'
                        ? 'bg-[#F5F5F5] text-[#18181B]'
                        : 'text-[#52525B] hover:bg-[#F5F5F5]'
                    }`}
                  >
                    <span className={`size-1.5 rounded-full ${statusFilter === 'inactive' ? 'bg-[#A1A1AA]' : 'bg-[#A1A1AA]/50'}`} />
                    Inactive
                    <span className={`text-[11px] ${statusFilter === 'inactive' ? 'text-[#52525B]' : 'text-[#A1A1AA]'}`}>{summary.inactive}</span>
                  </button>
                  <button
                    onClick={() => setStatusFilter(statusFilter === 'incomplete' ? 'all' : 'incomplete')}
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[12px] font-medium transition-colors cursor-pointer ${
                      statusFilter === 'incomplete'
                        ? 'bg-[#FEF9C3] text-[#854D0E]'
                        : 'text-[#52525B] hover:bg-[#F5F5F5]'
                    }`}
                  >
                    <span className={`size-1.5 rounded-full ${statusFilter === 'incomplete' ? 'bg-[#EAB308]' : 'bg-[#EAB308]/50'}`} />
                    Incomplete
                    <span className={`text-[11px] ${statusFilter === 'incomplete' ? 'text-[#854D0E]/70' : 'text-[#A1A1AA]'}`}>{summary.incomplete}</span>
                  </button>
                </div>
                <div className="relative">
                  <svg className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-[#A1A1AA]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
                  </svg>
                  <input
                    type="text"
                    placeholder="Search by name, ASIN, or SKU..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-72 h-9 pl-9 pr-3 rounded-lg border border-[#E5E5E5] text-[13px] text-[#18181B] placeholder:text-[#A1A1AA] focus:outline-none focus:ring-2 focus:ring-[#18181B]/10 focus:border-[#18181B]"
                  />
                </div>
              </div>

              {/* Table */}
              <div className="border border-[#E5E5E5] rounded-lg overflow-x-auto">
                <table className="w-full min-w-[900px] table-fixed">
                  <thead>
                    <tr className="bg-[#FAFAFA] border-b border-[#E5E5E5]">
                      <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-[#71717A] uppercase tracking-wider">Product</th>
                      <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-[#71717A] uppercase tracking-wider w-[120px]">ASIN</th>
                      <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-[#71717A] uppercase tracking-wider w-[180px]">SKU</th>
                      <th className="text-right px-4 py-2.5 text-[11px] font-semibold text-[#71717A] uppercase tracking-wider w-[120px]">Price</th>
                      <th className="text-center px-4 py-2.5 text-[11px] font-semibold text-[#71717A] uppercase tracking-wider w-[100px]">Status</th>
                      <th className="text-right pl-6 pr-4 py-2.5 text-[11px] font-semibold text-[#71717A] uppercase tracking-wider w-[280px]">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-4 py-12 text-center text-[13px] text-[#71717A]">
                          {search ? 'No listings match your search.' : 'No listings found.'}
                        </td>
                      </tr>
                    ) : (
                      filtered.map((listing, i) => {
                        const key = rowKey(listing, i)
                        const isExpanded = expandedKey === key
                        return (
                          <Fragment key={key}>
                            {/* Main row */}
                            <tr
                              className={`border-b border-[#F5F5F5] cursor-pointer transition-colors ${
                                isExpanded ? 'bg-[#FAFAFA]' : 'hover:bg-[#FAFAFA]'
                              }`}
                              onClick={() => handleRowClick(key)}
                            >
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-3">
                                  {listing.imageUrl ? (
                                    <img
                                      src={listing.imageUrl}
                                      alt=""
                                      className="size-10 rounded-md object-cover bg-[#F5F5F5] shrink-0"
                                      onError={(e) => {
                                        ;(e.target as HTMLImageElement).style.display = 'none'
                                      }}
                                    />
                                  ) : (
                                    <div className="size-10 rounded-md bg-[#F5F5F5] shrink-0 flex items-center justify-center">
                                      <svg className="size-5 text-[#D4D4D8]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0 0 22.5 18.75V5.25A2.25 2.25 0 0 0 20.25 3H3.75A2.25 2.25 0 0 0 1.5 5.25v13.5A2.25 2.25 0 0 0 3.75 21Z" />
                                      </svg>
                                    </div>
                                  )}
                                  <span className="text-[13px] text-[#18181B] font-medium line-clamp-2">{listing.itemName || '—'}</span>
                                </div>
                              </td>
                              <td className="px-4 py-3">
                                <a
                                  href={`https://www.amazon.com/dp/${listing.asin}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-[13px] text-[#2563EB] hover:underline font-mono"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {listing.asin}
                                </a>
                              </td>
                              <td className="px-4 py-3">
                                <span className="text-[12px] text-[#71717A] font-mono truncate block max-w-[180px]" title={listing.sellerSku}>
                                  {listing.sellerSku}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-right">
                                {(() => {
                                  const current = listing.currentPrice
                                  const regular = listing.regularPrice
                                  const listPrice = listing.price
                                  const salePrice = listing.salePrice
                                  const saleStart = listing.saleStartDate
                                  const saleEnd = listing.saleEndDate

                                  const hasSaleFromPricing = current && regular && parseFloat(current) < parseFloat(regular)
                                  const hasSaleFromReport = salePrice && listPrice && parseFloat(salePrice) < parseFloat(listPrice)
                                  const hasSale = hasSaleFromPricing || hasSaleFromReport

                                  const displayPrice = current || salePrice || listPrice
                                  const originalPrice = hasSaleFromPricing ? regular : hasSaleFromReport ? listPrice : ''

                                  if (!displayPrice) return <span className="text-[13px] text-[#A1A1AA]">—</span>

                                  const formatDate = (d: string) => {
                                    if (!d) return ''
                                    try {
                                      const date = new Date(d)
                                      if (isNaN(date.getTime())) return d
                                      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                                    } catch { return d }
                                  }

                                  const discountPct = hasSale && originalPrice
                                    ? Math.round((1 - parseFloat(hasSaleFromPricing ? current : salePrice) / parseFloat(originalPrice)) * 100)
                                    : 0

                                  if (hasSale) {
                                    const startStr = formatDate(saleStart)
                                    const endStr = formatDate(saleEnd)
                                    const dateRange = startStr && endStr ? `${startStr} – ${endStr}` : endStr ? `until ${endStr}` : startStr ? `from ${startStr}` : ''
                                    return (
                                      <div className="flex flex-col items-end gap-0.5">
                                        <div className="flex items-center gap-1.5">
                                          <span className="text-[13px] text-[#18181B] font-semibold">${hasSaleFromPricing ? current : salePrice}</span>
                                          {discountPct > 0 && (
                                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-[#F5F5F5] text-[#52525B]">
                                              -{discountPct}%
                                            </span>
                                          )}
                                        </div>
                                        <span className="text-[11px] text-[#A1A1AA] line-through">${originalPrice}</span>
                                        {dateRange ? (
                                          <span className="text-[10px] text-[#A1A1AA]">{dateRange}</span>
                                        ) : null}
                                      </div>
                                    )
                                  }
                                  return (
                                    <span className="text-[13px] text-[#18181B] font-medium">
                                      ${displayPrice}
                                    </span>
                                  )
                                })()}
                              </td>
                              <td className="px-4 py-3 text-center">
                                <StatusBadge status={listing.status} />
                              </td>
                              <td className="pl-6 pr-4 py-3 text-right">
                                <div className="flex items-center justify-end gap-2">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      navigate(pageLinks.AplusListingDetail(listing.asin), { state: { from: 'amazon-listings' } })
                                    }}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-medium text-[#18181B] bg-[#F5F5F5] hover:bg-[#E5E5E5] transition-colors cursor-pointer whitespace-nowrap"
                                  >
                                    <svg className="size-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75ZM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V8.625ZM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V4.125Z" />
                                    </svg>
                                    View A+
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      handleGenerateLabel(listing)
                                    }}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-medium text-[#18181B] bg-[#F5F5F5] hover:bg-[#E5E5E5] transition-colors cursor-pointer whitespace-nowrap"
                                  >
                                    <svg className="size-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M9.568 3H5.25A2.25 2.25 0 0 0 3 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 0 0 5.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 0 0 9.568 3Z" />
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 6h.008v.008H6V6Z" />
                                    </svg>
                                    Generate Label
                                  </button>
                                  {/* Chevron indicator */}
                                  <svg
                                    className={`size-4 text-[#A1A1AA] transition-transform duration-200 shrink-0 ${isExpanded ? 'rotate-180' : ''}`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                    strokeWidth={1.5}
                                  >
                                    <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
                                  </svg>
                                </div>
                              </td>
                            </tr>

                            {/* Expanded detail row */}
                            {isExpanded && (
                              <tr className="border-b border-[#E5E5E5]">
                                <td colSpan={6} className="p-0">
                                  <ListingDetail listing={listing} />
                                </td>
                              </tr>
                            )}
                          </Fragment>
                        )
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {/* Result count */}
              {(search || statusFilter !== 'all') && (
                <p className="mt-3 text-[12px] text-[#A1A1AA]">
                  Showing {filtered.length} of {listings.length} listings
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </AppShell>
  )
}
